import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { LocalizedStringComponent } from '@examdojo/core/i18n';
import { IconComponent } from '@examdojo/core/icon';
import { MatTooltipTruncateDirective } from '@examdojo/core/mat-tooltip-truncate';
import { StopPropagationDirective } from '@examdojo/core/stop-propagation';
import { finalizeWithValue, shareOneReplay } from '@examdojo/rxjs';
import { TranslocoPipe } from '@jsverse/transloco';
import { MtxTooltipModule } from '@ng-matero/extensions/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { CellMouseOutEvent, CellMouseOverEvent, ICellRendererParams, RowGroupOpenedEvent } from 'ag-grid-community';
import { BehaviorSubject, debounceTime, filter, firstValueFrom, map, merge, Observable, shareReplay, tap } from 'rxjs';
import { AssetTreeItem, AssetTreeNode, AssetTreeNodeType } from '../../../model';
import { TopicsTreeService } from '../../../topics-tree.service';
import { AssetExplorerService } from '../../asset-explorer.service';
import { AssetTreeBadgeComponent } from '../../asset-tree-badge/asset-tree-badge.component';
import { getNodeRouterLink, getPathRelativeToAssetsContainer, haveSameRootAsset } from '../../asset-tree-builder';
import { getUniqueUICode } from '../../asset-tree-builder/asset-tree-builder.utils';
import { AssetTreeSettingsService } from '../../asset-tree-settings.service';

@UntilDestroy()
@Component({
  selector: 'exam-asset-tree-node-cell-renderer',
  imports: [
    AssetTreeBadgeComponent,
    IconComponent,
    MtxTooltipModule,
    NgClass,
    StopPropagationDirective,
    RouterLink,
    RouterLinkActive,
    LocalizedStringComponent,
    TranslocoPipe,
    MatTooltipTruncateDirective,
    MatTooltip,
    // !Important
    // Each additional import and new components placed inside the template
    // may slow down the scroll rendering time of the asset tree as ag-grid
    // needs to create and render new component instances on the fly when scrolling.
    // The template+logic should be as light as possible!
    // Also splitting the template into multiple components causes performance issues and should be avoided.
  ],
  templateUrl: './asset-tree-node-cell-renderer.component.html',
  styleUrls: ['./asset-tree-node-cell-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetTreeNodeCellRendererComponent implements ICellRendererAngularComp {
  constructor(
    private readonly assetExplorerService: AssetExplorerService,
    private readonly assetTreeSettingsService: AssetTreeSettingsService,
    private readonly topicsTreeService: TopicsTreeService,
  ) {
    merge(this.bindAgGridActions(), this.updateExpandedWhenCellRendererParamsChange())
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  private readonly cellRendererParams$$ = new BehaviorSubject<ICellRendererParams<
    AssetTreeNode,
    unknown,
    unknown
  > | null>(null);

  readonly AssetTreeNodeType = AssetTreeNodeType;

  // update tree node every time the tree changes.
  // this is needed because the cell renderer params are updated only on tree structural changes.
  private readonly node$ = this.cellRendererParams$$.pipe(
    debounceTime(0),
    map((params) => params?.node.data),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly asset$: Observable<AssetTreeItem | undefined> = this.node$
    .pipe(map((node) => node?.asset))
    .pipe(shareOneReplay());

  readonly correlatedAssetHovered$$ = new BehaviorSubject(false);
  private readonly isExpanded$$ = new BehaviorSubject(false);

  readonly state = connectState({
    node: this.node$,
    cellRendererParams: this.cellRendererParams$$,
    badgeType: this.assetTreeSettingsService.badgeType$,
    asset: this.asset$,

    link: this.node$.pipe(map((node) => (node ? getNodeRouterLink(node) : null))),

    isTemporaryDirectory: this.node$.pipe(map((node) => node?.nodeType === AssetTreeNodeType.TemporaryDirectory)),
    expanded: this.isExpanded$$,

    nestingLevel: this.cellRendererParams$$.pipe(map((params) => (params?.node?.level ?? 1) - 1)),
    readonly: this.assetExplorerService.readonlyMode$,
    correlatedAssetHovered: this.correlatedAssetHovered$$,

    uiNameParts: this.node$.pipe(
      map((node) => {
        if (!node) {
          return { prefix: undefined, name: '' };
        }

        const code = node.asset ? getUniqueUICode(node.asset) : '';
        return {
          prefix: code || undefined,
          name: node.asset ? node.asset.name || '' : node.uiName || '',
        };
      }),
    ),
  });

  agInit(params: ICellRendererParams<AssetTreeNode, unknown>): void {
    this.cellRendererParams$$.next(params);
  }

  refresh(params: ICellRendererParams<AssetTreeNode, unknown>): boolean {
    this.cellRendererParams$$.next(params);
    return true;
  }

  async createAsset() {
    const path = await firstValueFrom(this.node$.pipe(filter(Boolean), map(getPathRelativeToAssetsContainer)));

    this.topicsTreeService.create(path);
  }

  async createDirectory() {
    const node = await firstValueFrom(this.node$);
    if (node) {
      this.assetExplorerService.createNewDirectory(node);
    }
  }

  async createRootAsset() {
    await this.assetExplorerService.createRootAsset();
  }

  async createRootDirectory() {
    await this.assetExplorerService.createRootDirectory();
  }

  private bindAgGridActions() {
    return this.cellRendererParams$$.pipe(
      tap((params) => this.setupListeners(params)),
      finalizeWithValue((params) => this.removeListeners(params)),
    );
  }

  private setupListeners(params: ICellRendererParams<AssetTreeNode, unknown, unknown> | null) {
    params?.api.addEventListener('rowGroupOpened', this.updateExpanded);
    params?.api.addEventListener('cellMouseOver', this.markIfCorrelatedAssetHovered);
    params?.api.addEventListener('cellMouseOut', this.clearCorrelatedAssetHovered);
  }

  private removeListeners(params: ICellRendererParams<AssetTreeNode, unknown, unknown> | null) {
    params?.api.removeEventListener('rowGroupOpened', this.updateExpanded);
    params?.api.removeEventListener('cellMouseOver', this.markIfCorrelatedAssetHovered);
    params?.api.removeEventListener('cellMouseOut', this.clearCorrelatedAssetHovered);
  }

  private readonly updateExpanded = (event: RowGroupOpenedEvent<AssetTreeNode>) => {
    if (event.node.data?.path === this.state.node?.path) {
      this.isExpanded$$.next(event.node.expanded);
    }
  };

  private updateExpandedWhenCellRendererParamsChange() {
    return this.cellRendererParams$$.pipe(
      tap((params) => {
        this.isExpanded$$.next(params?.node?.expanded ?? false);
      }),
    );
  }

  private readonly markIfCorrelatedAssetHovered = (event: CellMouseOverEvent<AssetTreeNode>) => {
    if (!event.data?.asset || !this.state.node) {
      return;
    }

    if (haveSameRootAsset(this.state.node, event.data)) {
      this.correlatedAssetHovered$$.next(true);
    }
  };

  private readonly clearCorrelatedAssetHovered = (event: CellMouseOutEvent<AssetTreeNode>) => {
    if (!event.data?.asset || !this.state.node || !this.state.asset) {
      this.correlatedAssetHovered$$.next(false);
      return;
    }

    if (haveSameRootAsset(this.state.node, event.data)) {
      this.correlatedAssetHovered$$.next(false);
    }
  };
}
